/**
 * EmailDraftDialog — manual-copy fallback for long email bodies.
 *
 * Gmail's mailto: redirect returns HTTP 400 around ~2 KB encoded URL,
 * Outlook web is similarly fussy, Apple Mail tolerates ~8 KB. The
 * full-review email for ~17 accounts hits ~10 KB encoded — past every
 * client's limit. This dialog presents the body for copy-paste
 * instead.
 *
 * Buttons:
 *   - Copy email body (primary): copies via copy-to-clipboard
 *     (works on HTTP dev hosts where navigator.clipboard rejects);
 *     swaps to "Copied" with a check icon for 2.2 s.
 *   - Open empty draft (secondary): mailto: with subject only, no
 *     body — lands the user on a blank draft to paste into.
 *   - Close: closes the dialog without doing anything.
 *
 * TEMP: orphan after FSD merge.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import { copyTextToClipboard, openSubjectOnlyDraft } from './emailContent';

const COPY_FEEDBACK_MS = 2200;

const useStyles = makeStyles((theme) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
  titleText: {
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  steps: {
    margin: theme.spacing(1, 0, 1.5),
    paddingLeft: theme.spacing(3),
    color: theme.palette.text.primary,
    fontSize: 14,
    '& li': { marginBottom: theme.spacing(0.5) },
  },
  subjectLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    display: 'block',
    marginBottom: theme.spacing(0.5),
  },
  subjectBlock: {
    background: '#f7f9fc',
    border: '1px solid #e3e8ef',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 1.5),
    fontSize: 13,
    fontFamily: 'monospace',
    wordBreak: 'break-word',
    color: theme.palette.text.primary,
  },
  copySuccessBtn: {
    background: theme.palette.success.main,
    color: theme.palette.common.white,
    '&:hover': { background: theme.palette.success.dark },
  },
  copyBtnIcon: { marginRight: theme.spacing(0.5) },
}));

const EmailDraftDialog = ({ open, subject, body, onClose }) => {
  const classes = useStyles();
  const [copied, setCopied] = useState(false);

  // Reset copied state when the dialog closes so the next open shows
  // the primary label, not a stale success state.
  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  // Auto-revert from "Copied" back to "Copy email body" after 2.2 s.
  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
    return () => clearTimeout(id);
  }, [copied]);

  const handleCopy = () => {
    if (!body) return;
    const ok = copyTextToClipboard(body);
    if (ok) setCopied(true);
  };

  const handleOpenEmpty = () => openSubjectOnlyDraft({ subject });

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="email-draft-fallback-title"
    >
      <DialogTitle id="email-draft-fallback-title" disableTypography>
        <Box className={classes.titleBar}>
          <span className={classes.titleText}>Email draft is too long for auto-fill</span>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          Gmail and Outlook web reject mailto: links larger than roughly 2 KB,
          and the full review for this many accounts is too long to auto-fill.
          Copy the body manually instead:
        </DialogContentText>
        <ol className={classes.steps}>
          <li>Copy the email body.</li>
          <li>Open your mail client (use the button below or your usual draft).</li>
          <li>Paste the body into the new draft.</li>
        </ol>
        <Box>
          <span className={classes.subjectLabel}>Subject preview</span>
          <Box className={classes.subjectBlock}>{subject || '—'}</Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={handleOpenEmpty} variant="outlined">
          Open empty draft
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          color="primary"
          className={copied ? classes.copySuccessBtn : undefined}
          disabled={!body}
          startIcon={copied ? <CheckIcon className={classes.copyBtnIcon} /> : null}
        >
          {copied ? 'Copied' : 'Copy email body'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDraftDialog;
